import { useEffect, useRef, useState } from 'react';
import type React from 'react';

import { HydrationBoundary, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Outlet } from 'react-router';

import { AppRoot, ThemeProvider } from '@change/ds-core-ui';

import { isSsr } from '@change-corgi/core/ssr';
import { Optimizely } from '@change-corgi/core-react/optimizely';
import { AppRenderInfoProvider, ClientRender } from '@change-corgi/core-react/ssr/render';
import { UtilityContextProvider } from '@change-corgi/core-react/utilityContext';

import { DSPrimitivesProvider, DSRouterProvider } from '#src/shared/ds';
import { PAGE_ROOT_ID } from '#src/shared/pageRoot';
import { SetPageStateSession } from '#src/shared/pageState';
import type { Session } from '#src/shared/session';

import { Banner } from '#src/app/app/banner';
import { CookieWall } from '#src/app/app/cookieWall';
import { DarkModeExclusion } from '#src/app/app/darkModeExclusion';
import { DarkModeRolloutExposure } from '#src/app/app/darkModeRolloutExposure';
import { SsrWarningBanner } from '#src/app/app/dev';
import { SkipToMainContent } from '#src/app/app/layout';
import type { LoadingContext } from '#src/app/app/topLoadingBar';
import { TopLoadingBar } from '#src/app/app/topLoadingBar';
import { UkSurgingPetitionToast } from '#src/app/app/ukSurgingPetitionToast';
import { useCurrentRouteConfig } from '#src/app/routes';
import { MetaPixel } from '#src/app/shared/components/metaPixel';
import { PerimeterXCaptchaHost } from '#src/app/shared/components/perimeterx';
import { useAttemptLoginByFacebook } from '#src/app/shared/hooks/auth';
import { useFocusFallback } from '#src/app/shared/hooks/focus';
import { L10nProvider, useCountryCode, useLocale } from '#src/app/shared/hooks/l10n';
import { CaptchaHostStore, CaptchaHostStoreProvider } from '#src/app/shared/hooks/perimeterX';
import {
	SessionProvider,
	useCookiePrefAsync,
	usePersistCookiePrefs,
	useSessionAsync,
} from '#src/app/shared/hooks/session';
import { SessionConfigurationProvider, useSessionConfiguration } from '#src/app/shared/hooks/sessionConfiguration';
import { UserAgentContextProvider } from '#src/app/shared/hooks/userAgent';
import { useWebappTrackLoad } from '#src/app/shared/hooks/webapp';
import { isLoaded } from '#src/app/shared/utils/async';
import { googleTagManagerTracking } from '#src/app/shared/utils/googleTagManager';

import { AppGlobalStyle } from './GlobalStyle';
import type { AppOptions } from './types';

const ThirdPartyUtilities: React.FC = () => {
	const { featureConfigs } = useSessionConfiguration();
	const analyticsCookies = useCookiePrefAsync('analytics');
	const marketingCookies = useCookiePrefAsync('marketing');

	useAttemptLoginByFacebook();

	useEffect(() => {
		if (!isLoaded(analyticsCookies)) return;
		googleTagManagerTracking.pushConsent('analytics', analyticsCookies.value ? 'granted' : 'denied');
	}, [analyticsCookies]);

	useEffect(() => {
		if (!isLoaded(marketingCookies)) return;
		googleTagManagerTracking.pushConsent('marketing', marketingCookies.value ? 'granted' : 'denied');
	}, [marketingCookies]);

	return (
		<>
			<ClientRender>
				{/* when no user data in SSR, we should wait for hydration to inject optimizely */}
				{/* otherwise, isOptOut: true will disable optimizely for all users even if they have approved cookies */}
				{/* we are also conditionally injecting the script here so that the optimizely state in the utility stays 'undefined' until the cookie prefs are available */}
				{isLoaded(analyticsCookies) && (
					<Optimizely
						allowed
						trackingAllowed={analyticsCookies.value}
						locationMatch={featureConfigs.optimizelyLocationMatch}
					/>
				)}
			</ClientRender>
			<MetaPixel />
		</>
	);
};

export const InnerApp: React.FC<
	Pick<AppOptions, 'utilities' | 'amplitudeGuidesAndSurveys' | 'amplitudeReplay'> & {
		loadingContext?: LoadingContext;
		// used to retrieve context for prefetching within the route config
		// TODO we should find a better way to handle this
		context: { session?: Session };
	}
> = ({ utilities, amplitudeGuidesAndSurveys, amplitudeReplay, context, loadingContext }) => {
	const analyticsCookies = useCookiePrefAsync('analytics');
	const session = useSessionAsync();
	const locale = useLocale();
	const countryCode = useCountryCode();
	const [captchaHostStore] = useState(() => new CaptchaHostStore());

	useEffect(() => {
		// session can be updated by something like the FB autologin
		if (isLoaded(session)) {
			// oxlint-disable-next-line no-param-reassign
			context.session = session.value; /* eslint-disable-line react-hooks/immutability */
		}
	}, [session, context]);

	// saves cookies prefs from localStorage to session when users logs in
	usePersistCookiePrefs();

	useEffect(() => {
		// as toggling is done in the bootstrapping as well, we want to avoid switching it to false while loading
		if (analyticsCookies.status === 'loaded') {
			utilities.googleAnalytics.toggle(analyticsCookies.value);
		}
	}, [utilities, analyticsCookies]);

	const currentRoute = useCurrentRouteConfig();

	useEffect(() => {
		if (!amplitudeReplay) return;
		const enabled = isLoaded(analyticsCookies) ? analyticsCookies.value : false;
		amplitudeReplay.setCurrentRoute(currentRoute?.config.id).setEnabled(enabled);
	}, [amplitudeReplay, currentRoute?.config.id, analyticsCookies]);

	useEffect(() => {
		if (!amplitudeGuidesAndSurveys) return;
		amplitudeGuidesAndSurveys.setEnabled(true);
	}, [amplitudeGuidesAndSurveys]);

	return (
		<CaptchaHostStoreProvider value={captchaHostStore}>
			<TopLoadingBar loadingContext={loadingContext} />
			<DarkModeExclusion />
			<DarkModeRolloutExposure />
			<PerimeterXCaptchaHost />
			<SetPageStateSession
				session={isLoaded(session) ? session.value : undefined}
				locale={locale}
				countryCode={countryCode}
			/>
			<ThirdPartyUtilities />
			<SkipToMainContent />
			{/* must be first element in DOM */}
			<CookieWall />
			<Banner />
			<UkSurgingPetitionToast />
			<Outlet />
		</CaptchaHostStoreProvider>
	);
};

export function App(appOptions: AppOptions): JSX.Element | null {
	const {
		utilities,
		l10n: { locale, countryCode },
		ssrContext,
		session,
		sessionConfiguration,
		sessionAvailableAtFirstRender,
		queryClient,
		dehydratedQueryClient,
		bot,
	} = appOptions;

	useWebappTrackLoad(appOptions);

	const bodyRef = useRef(isSsr() ? null : document.getElementById(PAGE_ROOT_ID));
	useFocusFallback(bodyRef, ({ focus, container }) =>
		focus(document.querySelector('[data-maincontent-principal]') || container, { preventScroll: true }),
	);

	// TODO split component to avoid following eslint comment

	return (
		<QueryClientProvider client={queryClient}>
			{process.env.ENABLE_DEV_TOOLS === 'true' && <ReactQueryDevtools initialIsOpen={false} />}
			<HydrationBoundary state={dehydratedQueryClient}>
				<ThemeProvider>
					<AppRoot>
						<DSPrimitivesProvider>
							<AppRenderInfoProvider>
								<L10nProvider locale={locale} countryCode={countryCode}>
									<UserAgentContextProvider bot={bot}>
										<SessionConfigurationProvider value={sessionConfiguration}>
											<SessionProvider session={session} sessionAvailableAtFirstRender={sessionAvailableAtFirstRender}>
												<AppGlobalStyle />
												<UtilityContextProvider utilities={utilities}>
													<DSRouterProvider>
														<SsrWarningBanner ssr={!!ssrContext} />
														<Outlet />
													</DSRouterProvider>
												</UtilityContextProvider>
											</SessionProvider>
										</SessionConfigurationProvider>
									</UserAgentContextProvider>
								</L10nProvider>
							</AppRenderInfoProvider>
						</DSPrimitivesProvider>
					</AppRoot>
				</ThemeProvider>
			</HydrationBoundary>
		</QueryClientProvider>
	);
}
